/**
 * Generic read-only data table (§18) — the shared list primitive for Shipments
 * and the Admin views, so every list looks and behaves the same. Static by
 * default; when `onRowOpen` is given, rows become keyboard-navigable (↑/↓ or
 * j/k move, Enter opens) with a roving tabindex + the shared focus treatment.
 * Sticky header, token-driven, density-aware. (The queue keeps its own
 * OrderTable for virtualization + priority rows.)
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { moveSelection } from "../../lib/keyboardNav.js";

export interface Column<T> {
  key: string;
  header: string;
  align?: "right";
  /** Cell renderer. */
  cell: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowOpen,
  selectedKey,
  ariaLabel,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  getRowKey: (row: T) => string;
  onRowOpen?: (row: T) => void;
  selectedKey?: string | null;
  ariaLabel: string;
}) {
  const interactive = Boolean(onRowOpen);
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const hasFocusRef = useRef(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const len = rows.length;

  useLayoutEffect(() => {
    if (!interactive || focusIndex < 0 || !hasFocusRef.current) return;
    const el = rowRefs.current.get(focusIndex);
    el?.focus();
    el?.scrollIntoView({ block: "nearest" });
  }, [focusIndex, interactive]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!interactive) return;
      // Only act when a ROW is focused — not an interior control (e.g. a tracking
      // link), so its own keys aren't hijacked and arrows don't double-fire.
      if ((e.target as HTMLElement).tagName !== "TR") return;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusIndex((i) => moveSelection(i, 1, len));
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusIndex((i) => moveSelection(i, -1, len));
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusIndex(len > 0 ? 0 : -1);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusIndex(len - 1);
      } else if (e.key === "Enter" && onRowOpen) {
        e.preventDefault();
        const idx = focusIndex >= 0 ? focusIndex : 0;
        const row = rows[idx];
        if (row) onRowOpen(row);
      }
    },
    [interactive, len, focusIndex, rows, onRowOpen],
  );

  // Reset the highlighted row when the row set changes (filter/page change).
  useEffect(() => {
    setFocusIndex((i) => (i >= len ? -1 : i));
  }, [len]);

  const rovingIndex = len === 0 ? -1 : focusIndex < 0 ? 0 : Math.min(focusIndex, len - 1);

  return (
    <div className="table-card">
      <div className="table-scroll">
        <table className="data-table" aria-label={ariaLabel}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.align === "right" ? "num" : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            onKeyDown={onKeyDown}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hasFocusRef.current = false;
            }}
          >
            {rows.map((row, index) => {
              const key = getRowKey(row);
              const selected = selectedKey != null && key === selectedKey;
              return (
                <tr
                  key={key}
                  ref={(el) => {
                    if (el) rowRefs.current.set(index, el);
                    else rowRefs.current.delete(index);
                  }}
                  className={"dt-row" + (interactive ? " dt-row--interactive" : "") + (selected ? " dt-row--selected" : "")}
                  tabIndex={interactive ? (index === rovingIndex ? 0 : -1) : undefined}
                  aria-current={selected ? "true" : undefined}
                  onClick={onRowOpen ? () => {
                    hasFocusRef.current = true;
                    setFocusIndex(index);
                    onRowOpen(row);
                  } : undefined}
                  onFocus={interactive ? () => {
                    hasFocusRef.current = true;
                    setFocusIndex(index);
                  } : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.align === "right" ? "num" : undefined}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
